import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { roleAllowed } from "@/lib/roles";
import { buildCustomerMap } from "@/lib/customerNames";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger, SheetFooter } from "@/components/ui/sheet";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Plus, PackageSearch, FileEdit, Truck, XCircle, ExternalLink, ClipboardCheck, RefreshCw, Boxes, AlertCircle, Printer, Download, PackageMinus, Sparkles, CheckCircle2, Ban } from "lucide-react";
import { toast } from "sonner";

const STATUS = {
  draft: "bg-slate-100 text-slate-600",
  packed: "bg-amber-50 text-amber-700",
  dispatched: "bg-emerald-50 text-emerald-700",
  cancelled: "bg-red-50 text-red-700",
};

const fmtDate = (d) => (d ? new Date(d).toLocaleDateString() : "—");
const toDateInput = (d) => (d ? new Date(d).toISOString().slice(0, 10) : "");

export default function PackingSlipsPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const [list, setList] = useState([]);
  const [csos, setCsos] = useState([]);
  const [items, setItems] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [customerNames, setCustomerNames] = useState([]);
  const [selected, setSelected] = useState(null);
  const [busy, setBusy] = useState(false);

  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [dateFilter, setDateFilter] = useState(""); // "packing slip updation against date" — filter the list by packing date

  const [open, setOpen] = useState(false);
  const [createForm, setCreateForm] = useState({ customer_so_id: "", packing_date: "", notes: "" });

  const [editOpen, setEditOpen] = useState(false);
  const [editForm, setEditForm] = useState({ lines: [], packing_date: "", notes: "" });

  const [cancelOpen, setCancelOpen] = useState(false);
  const [cancelReason, setCancelReason] = useState("");

  const [raiseDcOpen, setRaiseDcOpen] = useState(false);
  const [raiseDcForm, setRaiseDcForm] = useState({ vehicle_no: "", driver_name: "", transporter: "", notes: "" });
  // Ordered / Already on DCs / Remaining per line — from the same backend
  // calculation POST /raise-dc validates against, so the dialog and the API agree.
  const [dcAvailability, setDcAvailability] = useState(null);

  // Matches POST /packing-slips's role check.
  const canCreate = roleAllowed(user?.role, ["admin", "planner", "operator", "sales_crm", "marketing", "stores"]);
  // Edit/Cancel — the backend applies no role check on either (any user who
  // can reach this Packing Slip can adjust packed qty or cancel it before
  // dispatch), so these are just "is a logged-in user on this page".
  const canEdit = !!user;
  const canCancel = !!user;
  // Matches POST /packing-slips/{id}/raise-dc's role check.
  const canRaiseDc = roleAllowed(user?.role, ["admin", "planner", "operator"]);
  // Matches the approve-change/reject-change role check.
  const canApprove = roleAllowed(user?.role, ["admin", "planner", "production_manager"]);
  // Matches unpack/regenerate's role check.
  const canUnpack = roleAllowed(user?.role, ["admin", "planner", "operator", "production_manager", "stores"]);
  // Matches auto-generate/run's role check.
  const canRunAutoGenerate = roleAllowed(user?.role, ["admin", "planner", "production_manager", "stores"]);

  const [rejectOpen, setRejectOpen] = useState(false);
  const [rejectReason, setRejectReason] = useState("");

  const custMap = useMemo(() => buildCustomerMap(customers, customerNames), [customers, customerNames]);
  const itemMap = useMemo(() => Object.fromEntries(items.map((i) => [i.id, i])), [items]);

  const load = async () => {
    const [ps, cso, i, c, n] = await Promise.all([
      api.get("/packing-slips"),
      api.get("/customer-sales-orders"),
      api.get("/items"),
      api.get("/customers"),
      api.get("/customers/names-lookup"),
    ]);
    setList(ps.data);
    setCsos(cso.data);
    setItems(i.data);
    setCustomers(c.data);
    setCustomerNames(n.data);
    if (selected) setSelected(ps.data.find((x) => x.id === selected.id) || null);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  // Deep-link support — "View Packing Slip" from Sales Orders / Delivery Challans.
  useEffect(() => {
    const id = location.state?.selectId;
    if (id && list.length) {
      const found = list.find((x) => x.id === id);
      if (found) setSelected(found);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state?.selectId, list.length]);

  // "Create Packing Slip" from a Sales Order pre-selects it and opens the sheet.
  useEffect(() => {
    const cso = location.state?.presetCsoId;
    if (cso && csos.length) {
      setCreateForm((f) => ({ ...f, customer_so_id: cso }));
      setOpen(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state?.presetCsoId, csos.length]);

  // Eligible source Sales Orders — not cancelled, no Packing Slip currently
  // open against them, and something still left to pack (a Sales Order can
  // be packed over several batches, each going out on its own DC).
  const eligibleCsos = useMemo(
    () => csos.filter((s) => s.status !== "cancelled" && (s.fulfilment
      ? !s.fulfilment.open_packing_slip_id && s.fulfilment.pending_pack_qty > 0
      : !s.dc_id && !s.packing_slip_id)),
    [csos]
  );

  const visibleList = useMemo(() => {
    const q = search.trim().toLowerCase();
    return list.filter((ps) => {
      if (statusFilter === "pending") {
        if (ps.status === "cancelled" || !(ps.pending_qty > 0)) return false;
      } else if (statusFilter !== "all" && ps.status !== statusFilter) return false;
      if (dateFilter && toDateInput(ps.packing_date) !== dateFilter) return false;
      if (!q) return true;
      const cust = custMap[ps.customer_id];
      return (
        (ps.code || "").toLowerCase().includes(q) ||
        (ps.customer_so_code || "").toLowerCase().includes(q) ||
        (ps.dc_code || "").toLowerCase().includes(q) ||
        (cust?.name || ps.customer_name || "").toLowerCase().includes(q)
      );
    });
  }, [list, search, statusFilter, dateFilter, custMap]);

  const resetCreateForm = () => setCreateForm({ customer_so_id: "", packing_date: "", notes: "" });

  const create = async () => {
    if (!createForm.customer_so_id) return toast.error("Pick a Sales Order");
    setBusy(true);
    try {
      const { data } = await api.post("/packing-slips", {
        customer_so_id: createForm.customer_so_id,
        packing_date: createForm.packing_date ? new Date(createForm.packing_date).toISOString() : null,
        notes: createForm.notes,
      });
      toast.success(`Packing Slip ${data.code} created`);
      setOpen(false);
      resetCreateForm();
      await load();
      setSelected(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Create failed");
    } finally { setBusy(false); }
  };

  // Live FG stock this slip can pack (FG stock − other slips − stock blocked
  // for earlier Sales Orders) — the same figure the save is checked against.
  const [packable, setPackable] = useState({});
  const openEdit = (ps) => {
    setPackable({});
    api.get(`/packing-slips/${ps.id}/packable`).then(({ data }) => setPackable(data || {})).catch(() => {});
    setEditForm({
      lines: (ps.lines || []).map((l) => ({ item_id: l.item_id, packed_qty: l.packed_qty })),
      packing_date: toDateInput(ps.packing_date),
      notes: ps.notes || "",
    });
    setEditOpen(true);
  };

  const setEditLine = (idx, patch) => setEditForm((f) => ({
    ...f, lines: f.lines.map((l, i) => (i === idx ? { ...l, ...patch } : l)),
  }));

  const saveEdit = async () => {
    setBusy(true);
    try {
      await api.patch(`/packing-slips/${selected.id}`, {
        lines: editForm.lines,
        packing_date: editForm.packing_date ? new Date(editForm.packing_date).toISOString() : null,
        notes: editForm.notes,
      });
      toast.success(`${selected.code} updated`);
      setEditOpen(false);
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Update failed");
    } finally { setBusy(false); }
  };

  const refreshStock = async (ps) => {
    setBusy(true);
    try {
      await api.post(`/packing-slips/${ps.id}/refresh-stock`);
      toast.success("Packing plan refreshed against FG stock");
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Refresh failed");
    } finally { setBusy(false); }
  };

  const approveChange = async (ps) => {
    setBusy(true);
    try {
      await api.post(`/packing-slips/${ps.id}/approve-change`);
      toast.success(`${ps.code} — pending change approved`);
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Approve failed");
    } finally { setBusy(false); }
  };

  const submitRejectChange = async () => {
    setBusy(true);
    try {
      await api.post(`/packing-slips/${selected.id}/reject-change`, { reason: rejectReason });
      toast.success(`${selected.code} — pending change rejected`);
      setRejectOpen(false);
      setRejectReason("");
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Reject failed");
    } finally { setBusy(false); }
  };

  const unpack = async (ps) => {
    setBusy(true);
    try {
      await api.post(`/packing-slips/${ps.id}/unpack`);
      toast.success(`${ps.code} unpacked — back to draft`);
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Unpack failed");
    } finally { setBusy(false); }
  };

  const regenerate = async (ps) => {
    setBusy(true);
    try {
      await api.post(`/packing-slips/${ps.id}/regenerate`);
      toast.success(`${ps.code} regenerated against current FG stock`);
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Regenerate failed");
    } finally { setBusy(false); }
  };

  const runAutoGenerate = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/packing-slips/auto-generate/run");
      toast.success(data.count > 0 ? `Generated ${data.count} Packing Slip(s) (FIFO)` : "No eligible Sales Order to pack right now");
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Auto-generate failed");
    } finally { setBusy(false); }
  };

  const submitCancel = async () => {
    if (!cancelReason.trim()) return toast.error("A cancellation reason is required");
    setBusy(true);
    try {
      await api.post(`/packing-slips/${selected.id}/cancel`, { reason: cancelReason.trim() });
      toast.success(`${selected.code} cancelled`);
      setCancelOpen(false);
      setCancelReason("");
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Cancel failed");
    } finally { setBusy(false); }
  };

  const loadDcAvailability = async () => {
    try {
      const { data } = await api.get(`/packing-slips/${selected.id}/dc-availability`);
      setDcAvailability(data);
      return data;
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not load remaining quantities");
      return null;
    }
  };

  const openRaiseDc = async () => {
    setDcAvailability(null);
    setRaiseDcOpen(true);
    await loadDcAvailability();
  };

  const submitRaiseDc = async () => {
    setBusy(true);
    try {
      // Re-check against the latest backend quantities right before raising,
      // so a dialog left open never acts on stale numbers.
      const fresh = await loadDcAvailability();
      if (fresh && !fresh.ok) {
        toast.error("Packed qty exceeds the remaining qty on the order — see the table above");
        return;
      }
      const { data } = await api.post(`/packing-slips/${selected.id}/raise-dc`, raiseDcForm);
      toast.success(`Delivery Challan ${data.delivery_challan.code} raised`);
      if (data.pending_packing_slip) {
        toast.info(`Unpacked balance kept as Pending on ${data.pending_packing_slip.code} (batch ${data.pending_packing_slip.batch_no})`);
      }
      setRaiseDcOpen(false);
      setRaiseDcForm({ vehicle_no: "", driver_name: "", transporter: "", notes: "" });
      await load();
      navigate("/delivery-challans", { state: { selectId: data.delivery_challan.id } });
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Raise DC failed");
      await loadDcAvailability();
    } finally { setBusy(false); }
  };

  const viewSalesOrder = (ps) => navigate("/customer-sales-orders", { state: { selectId: ps.customer_so_id } });
  const viewDc = (ps) => navigate("/delivery-challans", { state: { selectId: ps.dc_id } });

  // Print just the selected Packing Slip's formatted document (same layout
  // as the PDF) in its own window — plain window.print() would print this
  // whole SPA page (list, nav, everything on screen), not the one slip
  // the user has open.
  const printPackingSlip = async (ps) => {
    try {
      const r = await api.get(`/packing-slips/${ps.id}/render-html`, { responseType: "text" });
      const w = window.open("", "_blank");
      w.document.write(`<html><head><title>${ps.code}</title></head><body>${r.data}<script>window.onload=()=>setTimeout(()=>window.print(),200)</script></body></html>`);
      w.document.close();
    } catch {
      toast.error("Print failed");
    }
  };

  const downloadPackingSlipPdf = async (ps) => {
    try {
      const res = await api.get(`/packing-slips/${ps.id}/pdf`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a"); a.href = url; a.download = `${ps.code}.pdf`;
      document.body.appendChild(a); a.click(); a.remove(); window.URL.revokeObjectURL(url);
      toast.success(`Downloaded ${ps.code}.pdf`);
    } catch {
      toast.error("PDF download failed");
    }
  };

  return (
    <div className="space-y-6" data-testid="ps-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="text-xs uppercase tracking-[0.18em] text-slate-500">Dispatch Planning</div>
          <h1 className="text-3xl sm:text-4xl tracking-tight font-extrabold text-slate-900" style={{ fontFamily: "Manrope" }}>
            Packing Slips
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Pack against a confirmed Sales Order's Finished Goods stock before raising the Delivery Challan.
          </p>
        </div>
        <div className="flex items-center gap-2">
          {canRunAutoGenerate && (
            <Button variant="outline" onClick={runAutoGenerate} disabled={busy} data-testid="ps-auto-generate-btn" title="Auto-generate Packing Slips for eligible Sales Order (FIFO against FG stock) — also runs automatically once daily">
              <Sparkles className="w-4 h-4 mr-1.5" />Run Auto-Generate
            </Button>
          )}
        {canCreate && (
          <Sheet open={open} onOpenChange={(v) => { setOpen(v); if (!v) resetCreateForm(); }}>
            <SheetTrigger asChild>
              <Button className="bg-blue-600 hover:bg-blue-700" data-testid="new-ps-btn"><Plus className="w-4 h-4 mr-1.5" />New Packing Slip</Button>
            </SheetTrigger>
            <SheetContent className="w-full sm:max-w-lg">
              <SheetHeader><SheetTitle>New Packing Slip</SheetTitle></SheetHeader>
              <div className="mt-6 space-y-4">
                <div>
                  <Label>Sales Order *</Label>
                  <Select value={createForm.customer_so_id} onValueChange={(v) => setCreateForm({ ...createForm, customer_so_id: v })}>
                    <SelectTrigger data-testid="ps-cso-select"><SelectValue placeholder="Pick a confirmed Sales Order" /></SelectTrigger>
                    <SelectContent>
                      {eligibleCsos.length === 0 && <SelectItem value="__none__" disabled>No eligible Sales Order</SelectItem>}
                      {eligibleCsos.map((s) => (
                        <SelectItem key={s.id} value={s.id}>{s.code} — {custMap[s.customer_id]?.name || s.customer_name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <div className="text-[11px] text-slate-500 mt-1">Lines and quantities are pre-filled from the Sales Order and stay editable after creation.</div>
                </div>
                <div>
                  <Label>Packing Date</Label>
                  <Input type="date" value={createForm.packing_date} onChange={(e) => setCreateForm({ ...createForm, packing_date: e.target.value })} data-testid="ps-create-date" />
                </div>
                <div>
                  <Label>Notes</Label>
                  <Textarea rows={2} value={createForm.notes} onChange={(e) => setCreateForm({ ...createForm, notes: e.target.value })} />
                </div>
              </div>
              <SheetFooter className="mt-6">
                <Button className="bg-blue-600 hover:bg-blue-700" onClick={create} disabled={busy} data-testid="create-ps-btn">Create Packing Slip</Button>
              </SheetFooter>
            </SheetContent>
          </Sheet>
        )}
        </div>
      </div>

      {/* Edit sheet — packed qty, packing date, notes */}
      <Sheet open={editOpen} onOpenChange={setEditOpen}>
        <SheetContent className="w-full sm:max-w-2xl overflow-y-auto">
          <SheetHeader><SheetTitle>Update {selected?.code}</SheetTitle></SheetHeader>
          <div className="mt-6 space-y-5">
            <div>
              <div className="text-sm font-semibold text-slate-700 mb-2">Line Items — packed qty editable</div>
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-[10px] uppercase tracking-[0.14em] text-slate-500">
                    <th className="py-2">Item</th><th className="py-2">To Pack</th>
                    <th className="py-2">FG Stock</th><th className="py-2">Free to Pack</th><th className="py-2">Packed Qty</th>
                  </tr>
                </thead>
                <tbody>
                  {(selected?.lines || []).map((sl, idx) => {
                    const ln = editForm.lines[idx] || {};
                    // Never more than the order's still-unpacked balance
                    // (ordered qty minus what every other batch packed).
                    const maxQty = sl.max_packable_qty ?? sl.ordered_qty;
                    return (
                      <tr key={sl.item_id} className="border-t border-slate-100">
                        <td className="py-2 mono">{itemMap[sl.item_id]?.code || sl.part_code} <span className="text-slate-500">— {itemMap[sl.item_id]?.name || sl.part_name}</span></td>
                        <td className="py-2 mono">{sl.ordered_qty}</td>
                        <td className="py-2 mono">{packable[sl.item_id]?.fg_stock ?? sl.fg_available_qty}</td>
                        <td className="py-2 mono" data-testid={`ps-edit-free-${idx}`}
                          title={packable[sl.item_id] ? [
                            `FG stock ${packable[sl.item_id].fg_stock}`,
                            ...(packable[sl.item_id].held_by_slips || []).map((h) => `− packed on ${h.code}: ${h.qty}`),
                            ...(packable[sl.item_id].blocked_for_orders || []).map((b) => `− blocked for ${b.order_no}: ${b.qty}`),
                            `= free to pack ${packable[sl.item_id].free}`,
                          ].join("\n") : undefined}>
                          {packable[sl.item_id] ? (
                            <span className={packable[sl.item_id].free < packable[sl.item_id].fg_stock ? "text-amber-700 font-semibold underline decoration-dotted cursor-help" : ""}>
                              {packable[sl.item_id].free}
                            </span>
                          ) : "…"}
                        </td>
                        <td className="py-2">
                          <Input
                            type="number" min={0} max={maxQty}
                            value={ln.packed_qty ?? sl.packed_qty}
                            onChange={(e) => setEditLine(idx, { packed_qty: Math.max(0, Math.min(parseFloat(e.target.value || 0), maxQty)) })}
                            className="w-24" data-testid={`ps-edit-packed-${idx}`}
                          />
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <div>
              <Label>Packing Date</Label>
              <Input type="date" value={editForm.packing_date} onChange={(e) => setEditForm({ ...editForm, packing_date: e.target.value })} data-testid="ps-edit-date" />
            </div>
            <div>
              <Label>Notes</Label>
              <Textarea rows={3} value={editForm.notes} onChange={(e) => setEditForm({ ...editForm, notes: e.target.value })} data-testid="ps-edit-notes" />
            </div>
          </div>
          <SheetFooter className="mt-6 gap-2">
            <Button variant="outline" onClick={() => setEditOpen(false)}>Cancel</Button>
            <Button onClick={saveEdit} disabled={busy} className="bg-blue-600 hover:bg-blue-700" data-testid="ps-edit-save">Save Changes</Button>
          </SheetFooter>
        </SheetContent>
      </Sheet>

      {/* Cancel dialog */}
      <Dialog open={cancelOpen} onOpenChange={setCancelOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Cancel {selected?.code}</DialogTitle></DialogHeader>
          <div className="space-y-2">
            <Label>Reason *</Label>
            <Textarea rows={3} value={cancelReason} onChange={(e) => setCancelReason(e.target.value)} placeholder="Why is this packing slip being cancelled?" data-testid="ps-cancel-reason" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCancelOpen(false)}>Back</Button>
            <Button variant="destructive" onClick={submitCancel} disabled={busy} data-testid="ps-cancel-confirm">
              <XCircle className="w-4 h-4 mr-1.5" />Confirm Cancel
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reject pending-change dialog */}
      <Dialog open={rejectOpen} onOpenChange={setRejectOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Reject pending change on {selected?.code}</DialogTitle></DialogHeader>
          <div className="space-y-2">
            <Label>Reason (optional)</Label>
            <Textarea rows={3} value={rejectReason} onChange={(e) => setRejectReason(e.target.value)} placeholder="Why is this change being rejected?" data-testid="ps-reject-reason" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRejectOpen(false)}>Back</Button>
            <Button variant="destructive" onClick={submitRejectChange} disabled={busy} data-testid="ps-reject-confirm">
              <Ban className="w-4 h-4 mr-1.5" />Reject Change
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Raise DC dialog */}
      <Dialog open={raiseDcOpen} onOpenChange={setRaiseDcOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>Raise Delivery Challan for {selected?.code}</DialogTitle></DialogHeader>
          <div className="text-xs text-slate-500 -mt-2 mb-1">
            Dispatches exactly what's been packed on this slip — edit packed quantities above before raising the DC.
          </div>
          {dcAvailability && (
            <div className="border rounded-md overflow-x-auto" data-testid="ps-dc-availability">
              <table className="w-full text-xs">
                <thead className="bg-slate-50 text-slate-500">
                  <tr>
                    <th className="text-left px-2 py-1">Item</th>
                    <th className="text-right px-2 py-1">Ordered</th>
                    <th className="text-right px-2 py-1">Already Delivered</th>
                    <th className="text-right px-2 py-1">Remaining</th>
                    <th className="text-right px-2 py-1">Current Packed Qty</th>
                  </tr>
                </thead>
                <tbody>
                  {dcAvailability.lines.map((l) => (
                    <tr key={l.item_id} className={l.ok ? "" : "bg-red-50 text-red-700"}>
                      <td className="px-2 py-1">
                        {l.part_code || l.item_id}
                        {l.dcs.length > 0 && (
                          <div className="text-[10px] text-slate-500">
                            {l.dcs.map((d) => `${d.code} (${d.status === "draft" ? "ready" : d.status}, ${d.qty})`).join(", ")}
                          </div>
                        )}
                      </td>
                      <td className="text-right px-2 py-1">
                        {l.ordered_qty}
                        {l.to_ordered_qty !== l.ordered_qty && (
                          <div className="text-[10px] text-amber-600" title="The linked Transfer Order line has a different qty than this Sales Order">TO: {l.to_ordered_qty}</div>
                        )}
                      </td>
                      <td className="text-right px-2 py-1">{l.previously_delivered_qty}</td>
                      <td className="text-right px-2 py-1 font-medium">{l.remaining_qty}</td>
                      <td className="text-right px-2 py-1">{l.current_packed_qty}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {!dcAvailability.ok && (
                <div className="flex items-start gap-1.5 text-xs text-red-700 px-2 py-1.5 border-t" data-testid="ps-dc-over-remaining">
                  <AlertCircle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                  Packed qty exceeds what is left on the order — reduce the packed qty to the Remaining qty (or correct the Packing Slip) before raising the DC.
                </div>
              )}
            </div>
          )}
          <div className="space-y-3">
            <div><Label>Vehicle No</Label><Input value={raiseDcForm.vehicle_no} onChange={(e) => setRaiseDcForm({ ...raiseDcForm, vehicle_no: e.target.value })} data-testid="ps-dc-vehicle" /></div>
            <div><Label>Driver Name</Label><Input value={raiseDcForm.driver_name} onChange={(e) => setRaiseDcForm({ ...raiseDcForm, driver_name: e.target.value })} /></div>
            <div><Label>Transporter</Label><Input value={raiseDcForm.transporter} onChange={(e) => setRaiseDcForm({ ...raiseDcForm, transporter: e.target.value })} /></div>
            <div><Label>Notes</Label><Textarea rows={2} value={raiseDcForm.notes} onChange={(e) => setRaiseDcForm({ ...raiseDcForm, notes: e.target.value })} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRaiseDcOpen(false)}>Cancel</Button>
            <Button onClick={submitRaiseDc} disabled={busy || (dcAvailability && !dcAvailability.ok)} className="bg-blue-600 hover:bg-blue-700" data-testid="ps-dc-submit">
              <Truck className="w-4 h-4 mr-1.5" />Raise DC
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
        <div className="lg:col-span-2 bg-white border border-slate-200 rounded-lg overflow-hidden">
          <div className="px-4 py-3 border-b border-slate-100 text-sm font-semibold text-slate-700 flex justify-between">
            <span>Packing Slips</span><span className="text-xs text-slate-500 mono">{visibleList.length}/{list.length}</span>
          </div>
          <div className="px-3 py-2 border-b border-slate-100 space-y-2">
            <Input
              placeholder="Search by PS/SO/DC code, customer..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="h-8 text-sm"
              data-testid="ps-search-input"
            />
            <div className="grid grid-cols-2 gap-2">
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="h-8 text-xs" data-testid="ps-status-filter"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All statuses</SelectItem>
                  <SelectItem value="pending">Pending (qty left to pack)</SelectItem>
                  <SelectItem value="draft">Draft</SelectItem>
                  <SelectItem value="packed">Packed</SelectItem>
                  <SelectItem value="dispatched">Dispatched</SelectItem>
                  <SelectItem value="cancelled">Cancelled</SelectItem>
                </SelectContent>
              </Select>
              {/* Packing slip updation against date — filter the list to one packing date */}
              <Input type="date" value={dateFilter} onChange={(e) => setDateFilter(e.target.value)} className="h-8 text-xs" data-testid="ps-date-filter" />
            </div>
          </div>
          <div className="divide-y divide-slate-100 max-h-[70vh] overflow-y-auto scroll-thin">
            {visibleList.length === 0 && <div className="px-4 py-10 text-center text-sm text-slate-500" data-testid="no-pss">No packing slips match.</div>}
            {visibleList.map((ps) => (
              <button key={ps.id} onClick={() => setSelected(ps)} data-testid={`ps-row-${ps.code}`}
                className={`w-full text-left px-4 py-3 hover:bg-slate-50 transition ${selected?.id === ps.id ? "bg-blue-50/60" : ""} ${ps.status === "cancelled" ? "opacity-60" : ""}`}>
                <div className="flex items-center justify-between">
                  <div className="mono text-xs text-slate-500 uppercase tracking-[0.14em]">
                    {ps.code}{ps.batch_no > 1 ? <span className="ml-1.5 normal-case tracking-normal text-slate-400">· Batch {ps.batch_no}</span> : null}
                  </div>
                  <div className="flex items-center gap-1">
                    {ps.status !== "cancelled" && ps.pending_qty > 0 && (
                      <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-amber-50 text-amber-700" data-testid={`ps-pending-badge-${ps.code}`}>
                        {ps.pending_qty} pending
                      </span>
                    )}
                    <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full ${STATUS[ps.status]}`}>{ps.status}</span>
                  </div>
                </div>
                <div className="mt-1 text-sm font-medium text-slate-800">{custMap[ps.customer_id]?.name || ps.customer_name}</div>
                <div className="mt-0.5 text-xs text-slate-500">
                  SO <span className="mono text-blue-700">{ps.customer_so_code}</span> · {ps.lines?.length || 0} line(s)
                  {ps.packing_date ? ` · Packing ${fmtDate(ps.packing_date)}` : ""}
                  {ps.dc_code ? <> · DC <span className="mono text-emerald-700">{ps.dc_code}</span></> : ""}
                </div>
              </button>
            ))}
          </div>
        </div>

        <div className="lg:col-span-3 bg-white border border-slate-200 rounded-lg p-6 min-h-[60vh]" data-testid="ps-detail">
          {!selected ? (
            <div className="h-full flex flex-col items-center justify-center text-sm text-slate-500 gap-2">
              <PackageSearch className="w-10 h-10 text-slate-300" /> Select a packing slip to view details.
            </div>
          ) : (
            <div className="space-y-5">
              <div className="flex items-start justify-between flex-wrap gap-3">
                <div>
                  <div className="text-xs uppercase tracking-[0.14em] text-slate-500">{selected.code}</div>
                  <h2 className="text-2xl font-extrabold tracking-tight text-slate-900" style={{ fontFamily: "Manrope" }}>
                    {custMap[selected.customer_id]?.name || selected.customer_name || "—"}
                  </h2>
                  <div className="text-xs text-slate-500 mt-1 flex flex-wrap items-center gap-x-1">
                    Sales Order:{" "}
                    <button type="button" onClick={() => viewSalesOrder(selected)} className="mono text-blue-700 hover:underline inline-flex items-center gap-0.5" data-testid="ps-so-link">
                      {selected.customer_so_code}<ExternalLink className="w-3 h-3" />
                    </button>
                    {selected.dc_code && (
                      <>
                        {" · "}Delivery Challan:{" "}
                        <button type="button" onClick={() => viewDc(selected)} className="mono text-blue-700 hover:underline inline-flex items-center gap-0.5" data-testid="ps-dc-link">
                          {selected.dc_code}<ExternalLink className="w-3 h-3" />
                        </button>
                      </>
                    )}
                    {" · "}Packing Date: <span className="mono">{fmtDate(selected.packing_date)}</span>
                    {" · "}Batch <span className="mono" data-testid="ps-batch-no">{selected.batch_no || 1}</span>
                  </div>
                </div>
                <div className="flex flex-col items-end gap-1">
                  <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full ${STATUS[selected.status]}`}>{selected.status}</span>
                  {selected.auto_generated && (
                    <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-violet-50 text-violet-700 flex items-center gap-1" data-testid="ps-auto-generated-badge">
                      <Sparkles className="w-3 h-3" />Auto-generated
                    </span>
                  )}
                </div>
              </div>

              {selected.status === "cancelled" && (
                <div className="flex items-start gap-2 bg-red-50 border border-red-200 text-red-700 text-xs px-3 py-2 rounded-md" data-testid="ps-cancelled-banner">
                  <AlertCircle className="w-3.5 h-3.5 shrink-0 mt-0.5" />
                  <div>
                    <div className="font-semibold">Cancelled{selected.cancelled_by ? ` by ${selected.cancelled_by}` : ""}{selected.cancelled_at ? ` on ${new Date(selected.cancelled_at).toLocaleString()}` : ""}</div>
                    {selected.cancel_reason && <div>Reason: {selected.cancel_reason}</div>}
                  </div>
                </div>
              )}

              {selected.status !== "cancelled" && selected.pending_qty > 0 && (
                <div className="flex items-start gap-2 bg-amber-50 border border-amber-200 text-amber-800 text-xs px-3 py-2 rounded-md" data-testid="ps-pending-qty-banner">
                  <AlertCircle className="w-3.5 h-3.5 shrink-0 mt-0.5" />
                  <div>
                    <div className="font-semibold">Pending — {selected.pending_qty} still to pack on Sales Order {selected.customer_so_code}</div>
                    <div>
                      {selected.status === "dispatched"
                        ? "This batch has gone out on its DC; the balance stays here as Pending and can be packed and dispatched on a separate DC."
                        : "Raising a DC now dispatches only the packed qty; the balance stays Pending for a later batch and DC."}
                    </div>
                  </div>
                </div>
              )}

              {selected.pending_change && (
                <div className="flex items-start justify-between gap-3 flex-wrap bg-amber-50 border border-amber-200 text-amber-800 text-xs px-3 py-2 rounded-md" data-testid="ps-pending-change-banner">
                  <div className="flex items-start gap-2">
                    <AlertCircle className="w-3.5 h-3.5 shrink-0 mt-0.5" />
                    <div>
                      <div className="font-semibold">Change pending approval</div>
                      <div>Requested by {selected.pending_change.requested_by || "—"} on {selected.pending_change.requested_at ? new Date(selected.pending_change.requested_at).toLocaleString() : "—"}</div>
                    </div>
                  </div>
                  {canApprove && (
                    <div className="flex items-center gap-2">
                      <Button size="sm" className="h-7 bg-emerald-600 hover:bg-emerald-700" onClick={() => approveChange(selected)} disabled={busy} data-testid="ps-approve-change-btn">
                        <CheckCircle2 className="w-3.5 h-3.5 mr-1" />Approve
                      </Button>
                      <Button size="sm" variant="outline" className="h-7 text-red-600 hover:text-red-700" onClick={() => setRejectOpen(true)} disabled={busy} data-testid="ps-reject-change-btn">
                        <Ban className="w-3.5 h-3.5 mr-1" />Reject
                      </Button>
                    </div>
                  )}
                </div>
              )}

              <div className="flex items-center gap-2 flex-wrap" data-testid="ps-actions">
                {canEdit && !["dispatched", "cancelled"].includes(selected.status) && (
                  <Button variant="outline" size="sm" onClick={() => openEdit(selected)} data-testid="ps-edit-btn">
                    <FileEdit className="w-4 h-4 mr-1.5" />Update
                  </Button>
                )}
                {!["dispatched", "cancelled"].includes(selected.status) && (
                  <Button variant="outline" size="sm" onClick={() => refreshStock(selected)} disabled={busy} data-testid="ps-refresh-btn">
                    <RefreshCw className="w-4 h-4 mr-1.5" />Refresh FG Stock
                  </Button>
                )}
                {canUnpack && selected.status === "packed" && (
                  <Button variant="outline" size="sm" onClick={() => unpack(selected)} disabled={busy} data-testid="ps-unpack-btn">
                    <PackageMinus className="w-4 h-4 mr-1.5" />Unpack
                  </Button>
                )}
                {canUnpack && !["dispatched", "cancelled"].includes(selected.status) && (
                  <Button variant="outline" size="sm" onClick={() => regenerate(selected)} disabled={busy} data-testid="ps-regenerate-btn" title="Re-snapshot from the Sales Order + current FG stock (FIFO)">
                    <Sparkles className="w-4 h-4 mr-1.5" />Regenerate
                  </Button>
                )}
                {!selected.dc_id && !["cancelled"].includes(selected.status) && canRaiseDc && (
                  <Button size="sm" onClick={openRaiseDc} className="bg-blue-600 hover:bg-blue-700" data-testid="ps-raise-dc-btn">
                    <Truck className="w-4 h-4 mr-1.5" />Raise DC
                  </Button>
                )}
                {selected.dc_id && (
                  <Button variant="outline" size="sm" onClick={() => viewDc(selected)} data-testid="ps-view-dc-btn">
                    <ClipboardCheck className="w-4 h-4 mr-1.5" />View DC {selected.dc_code}
                  </Button>
                )}
                <Button variant="outline" size="sm" onClick={() => printPackingSlip(selected)} data-testid="ps-print-btn">
                  <Printer className="w-4 h-4 mr-1.5" />Print
                </Button>
                <Button variant="outline" size="sm" onClick={() => downloadPackingSlipPdf(selected)} data-testid="ps-pdf-btn">
                  <Download className="w-4 h-4 mr-1.5" />PDF
                </Button>
                {canCancel && !["dispatched", "cancelled"].includes(selected.status) && (
                  <Button variant="outline" size="sm" onClick={() => setCancelOpen(true)} className="ml-auto text-red-600 hover:text-red-700" data-testid="ps-cancel-btn">
                    <XCircle className="w-4 h-4 mr-1.5" />Cancel
                  </Button>
                )}
              </div>

              {selected.notes && (
                <div className="text-sm text-slate-600 border-l-2 border-blue-300 pl-3 italic" data-testid="ps-notes">{selected.notes}</div>
              )}

              <div>
                <div className="text-xs uppercase tracking-[0.14em] text-slate-500 mb-2 flex items-center gap-1.5">
                  <Boxes className="w-3.5 h-3.5" /> Packing Plan — against Finished Goods stock
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-left text-[10px] uppercase tracking-[0.14em] text-slate-500 bg-slate-50">
                        <th className="py-2 px-3">Part No</th>
                        <th className="py-2 px-3">Item</th>
                        <th className="py-2 px-3">Ordered</th>
                        <th className="py-2 px-3">To Pack (this batch)</th>
                        <th className="py-2 px-3">FG Free to Pack</th>
                        <th className="py-2 px-3">Packed Qty</th>
                        <th className="py-2 px-3">Pending (order)</th>
                        <th className="py-2 px-3">Delivery Date</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(selected.lines || []).map((l, idx) => {
                        const short = l.packed_qty > l.fg_available_qty;
                        return (
                          <tr key={idx} className="border-t border-slate-100" data-testid={`ps-line-${idx}`}>
                            <td className="py-2 px-3 mono text-xs text-slate-500">{itemMap[l.item_id]?.code || l.part_code || "—"}</td>
                            <td className="py-2 px-3 text-slate-700">{itemMap[l.item_id]?.name || l.part_name}</td>
                            <td className="py-2 px-3 mono">{l.order_qty || l.ordered_qty} {l.uom}</td>
                            <td className="py-2 px-3 mono">{l.ordered_qty}</td>
                            <td className={`py-2 px-3 mono ${short ? "text-amber-700 font-semibold" : ""}`} title={short ? "Packed quantity exceeds FG stock on hand" : undefined}>
                              {l.fg_available_qty}
                            </td>
                            <td className="py-2 px-3 mono font-semibold">{l.packed_qty}</td>
                            <td className={`py-2 px-3 mono ${l.pending_qty > 0 ? "text-amber-700 font-semibold" : "text-slate-500"}`} data-testid={`ps-line-pending-${idx}`}>
                              {l.pending_qty ?? "—"}
                            </td>
                            <td className="py-2 px-3 mono text-xs text-slate-700">{fmtDate(l.delivery_date)}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>

              <div className="text-xs text-slate-500">
                Flow: <span className="mono">draft → packed → dispatched</span>. Raising a DC dispatches this batch's packed qty, attaches the DC number here and locks this slip; any unpacked balance moves to the next batch's slip as Pending.
              </div>

              <div className="text-[11px] text-slate-400 border-t border-slate-100 pt-2" data-testid="ps-detail-audit">
                Created by <span className="mono">{selected.created_by || "—"}</span> on {selected.created_at ? new Date(selected.created_at).toLocaleString() : "—"}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
